import { html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import {
  SubscribeAsyncPrice,
  subscribeAsyncPriceContext,
  UnsubscribeAsyncPrice,
  unsubscribeAsyncPriceContext,
} from 'components/ordering/async-price/components/async-price-context.js'
import { consume } from '@lit/context'
import { required } from 'components/shared/required/required.js'

import { PriceDTO } from 'components/ordering/async-price/api/price-dto.js'


@customElement('csc-async-price')
export class CSCAsyncPrice extends LitElement {
  @required()
  @property({ attribute: 'product-id', type: String })
  public productId!: string

  @state()
  private formattedPrice: string | null = null

  @state()
  private uom: string | null = null

  @state()
  private formattedPriceUomSale: string | null = null

  @state()
  private priceUom: string | null = null

  @state()
  private pricePerPriceUom: string | null = null

  @consume({ context: subscribeAsyncPriceContext })
  private subscribeAsyncPrice!: SubscribeAsyncPrice

  @consume({ context: unsubscribeAsyncPriceContext })
  private unsubscribeAsyncPrice!: UnsubscribeAsyncPrice

  override disconnectedCallback() {
    this.unsubscribe()
    super.disconnectedCallback()
  }

  protected override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('productId')) {
      this.unsubscribe()
      this.resetState()
      this.subscribe()
    }
    super.willUpdate(changedProperties)
  }

  private subscribe = () => {
    this.subscribeAsyncPrice(this, {
      productId: this.productId,
      callback: this.updatePrice,
    })
  }

  private unsubscribe = () => {
    this.unsubscribeAsyncPrice(this)
  }

  private resetState = () => {
    this.formattedPrice = null
    this.uom = null
    this.formattedPriceUomSale = null
    this.priceUom = null
    this.pricePerPriceUom = null
  }

  private updatePrice = (priceDto: PriceDTO) => {
    this.formattedPrice = priceDto.sale
    this.uom = priceDto.uom
    this.formattedPriceUomSale = priceDto.priceUomSale
    this.priceUom = priceDto.priceUom
    this.pricePerPriceUom = priceDto.pricePerPriceUom
  }

  protected override render = () => {
    if (this.formattedPrice === null) return this.renderSkeleton()

    return html`
      ${this.renderPrice()}
      ${this.renderPriceUom()}
      ${this.renderPricePerPriceUom()}
    `
  }

  private renderPrice = () => {
    return html`
      <cse-price
        as="p"
        size="m"
        layout="inline-meta-below"
        uom="${this.uom}">
        ${this.formattedPrice}
      </cse-price>
    `
  }

  private renderPriceUom = () => {
    if (this.formattedPriceUomSale === null) return nothing
    return html`
      <cse-price
        as="p"
        size="s"
        layout="inline-meta-below"
        uom="${this.priceUom}">
        ${this.formattedPriceUomSale}
      </cse-price>
    `
  }

  private renderPricePerPriceUom = () => {
    if (this.pricePerPriceUom === null) return nothing
    return html`
      <cse-text variant="caption-m">
        ${this.pricePerPriceUom} / ${this.priceUom ?? this.uom}
      </cse-text>
    `
  }

  private renderSkeleton = () => {
    return html`
      <cse-skeleton
        variant="secondary"
        width="120">
      </cse-skeleton>
    `
  }
}
